import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { defaultSettings, writeSettingsCache } from '../../hooks/useSiteSettings';
import ImageUploadField from '../../components/admin/ImageUploadField';
import VideoUploadField from '../../components/admin/VideoUploadField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: 'hero', label: '🏠 Home Hero' },
  { id: 'valori', label: '🌿 Valori & Preview' },
  { id: 'prodotti', label: '🛒 Prodotti Info' },
  { id: 'negozio', label: '🛍️ Negozio' },
  { id: 'news', label: '📰 News' },
  { id: 'recensioni', label: '⭐ Recensioni' },
  { id: 'chi_storia', label: '📖 Chi Siamo - Storia' },
  { id: 'chi_territorio', label: '🌋 Chi Siamo - Territorio' },
  { id: 'timeline', label: '📅 Timeline' },
  { id: 'contatti', label: '📬 Contatti' },
  { id: 'video_intro', label: '🎬 Video Intro' },
  { id: 'galleria', label: '🖼️ Galleria' },
];

const SECTION_FIELDS = {
  hero: ['logo_url', 'hero_image_url', 'hero_badge', 'hero_title', 'hero_subtitle'],
  valori: ['value1_title', 'value1_text', 'value2_title', 'value2_text', 'value3_title', 'value3_text', 'about_label', 'about_title', 'about_text', 'about_image_url', 'cta_title', 'cta_text'],
  prodotti: ['prodotti_info_label', 'prodotti_info_title', 'prodotti_info_p1', 'prodotti_info_p2', 'prodotti_info_p3', 'prodotti_info_img1_url', 'prodotti_info_img2_url', 'prodotti_info_img3_url', 'prodotti_info_img4_url'],
  negozio: ['negozio_hero_image_url'],
  news: ['news_hero_image_url'],
  recensioni: ['recensioni_hero_image_url'],
  chi_storia: ['chi_siamo_hero_image_url', 'chi_siamo_story_image_url', 'chi_siamo_story_title', 'chi_siamo_story_p1', 'chi_siamo_story_p2', 'chi_siamo_story_p3'],
  chi_territorio: ['chi_siamo_territory_title', 'chi_siamo_territory_p1', 'chi_siamo_territory_p2', 'chi_siamo_territory_p3', 'chi_siamo_territory_image_url'],
  timeline: ['timeline_0_year', 'timeline_0_title', 'timeline_0_desc', 'timeline_1_year', 'timeline_1_title', 'timeline_1_desc', 'timeline_2_year', 'timeline_2_title', 'timeline_2_desc', 'timeline_3_year', 'timeline_3_title', 'timeline_3_desc'],
  contatti: ['phone', 'whatsapp_number', 'email_address', 'address', 'orari', 'facebook_url', 'maps_url', 'contatti_hero_image_url'],
  video_intro: ['intro_video_url'],
  galleria: ['galleria_hero_image_url'],
};

const FIELD_LABELS = {
  logo_url: 'Logo Sito (navbar e footer)',
  hero_image_url: 'URL Immagine Hero (sfondo)',
  hero_badge: 'Badge / Sottotitolo Hero',
  hero_title: 'Titolo principale Hero',
  hero_subtitle: 'Sottotitolo Hero',
  value1_title: 'Valore 1 — Titolo', value1_text: 'Valore 1 — Testo',
  value2_title: 'Valore 2 — Titolo', value2_text: 'Valore 2 — Testo',
  value3_title: 'Valore 3 — Titolo', value3_text: 'Valore 3 — Testo',
  about_label: 'Preview Chi Siamo — Etichetta',
  about_title: 'Preview Chi Siamo — Titolo',
  about_text: 'Preview Chi Siamo — Testo',
  about_image_url: 'Preview Chi Siamo — URL Immagine',
  cta_title: 'CTA Finale — Titolo',
  cta_text: 'CTA Finale — Testo',
  chi_siamo_hero_image_url: 'Chi Siamo — URL Immagine Hero',
  chi_siamo_story_image_url: 'Chi Siamo — URL Immagine Storia',
  chi_siamo_story_title: 'Chi Siamo — Titolo Storia',
  chi_siamo_story_p1: 'Chi Siamo — Paragrafo 1',
  chi_siamo_story_p2: 'Chi Siamo — Paragrafo 2',
  chi_siamo_story_p3: 'Chi Siamo — Paragrafo 3',
  chi_siamo_territory_title: 'Territorio — Titolo',
  chi_siamo_territory_p1: 'Territorio — Paragrafo 1',
  chi_siamo_territory_p2: 'Territorio — Paragrafo 2',
  chi_siamo_territory_p3: 'Territorio — Paragrafo 3',
  chi_siamo_territory_image_url: 'Territorio — URL Immagine',
  timeline_0_year: 'Evento 1 — Anno', timeline_0_title: 'Evento 1 — Titolo', timeline_0_desc: 'Evento 1 — Descrizione',
  timeline_1_year: 'Evento 2 — Anno', timeline_1_title: 'Evento 2 — Titolo', timeline_1_desc: 'Evento 2 — Descrizione',
  timeline_2_year: 'Evento 3 — Anno', timeline_2_title: 'Evento 3 — Titolo', timeline_2_desc: 'Evento 3 — Descrizione',
  timeline_3_year: 'Evento 4 — Anno', timeline_3_title: 'Evento 4 — Titolo', timeline_3_desc: 'Evento 4 — Descrizione',
  phone: 'Telefono (visualizzato)',
  whatsapp_number: 'Numero WhatsApp (solo cifre, es: 393382523925)',
  email_address: 'Email',
  address: 'Indirizzo',
  orari: 'Orari di apertura',
  facebook_url: 'URL Pagina Facebook',
  maps_url: 'URL Google Maps',
  contatti_hero_image_url: 'Contatti — URL Immagine Hero',
  prodotti_info_label: 'Prodotti Info — Etichetta',
  prodotti_info_title: 'Prodotti Info — Titolo',
  prodotti_info_p1: 'Prodotti Info — Paragrafo 1',
  prodotti_info_p2: 'Prodotti Info — Paragrafo 2',
  prodotti_info_p3: 'Prodotti Info — Paragrafo 3',
  prodotti_info_img1_url: 'Prodotti Info — Immagine 1 (in alto a sinistra)',
  prodotti_info_img2_url: 'Prodotti Info — Immagine 2 (in basso a sinistra)',
  prodotti_info_img3_url: 'Prodotti Info — Immagine 3 (in alto a destra)',
  prodotti_info_img4_url: 'Prodotti Info — Immagine 4 (in basso a destra)',
  intro_video_url: 'Video di Presentazione (mostrato una volta per sessione)',
  galleria_hero_image_url: 'Galleria — URL Immagine Hero (sfondo)',
  negozio_hero_image_url: 'Negozio — URL Immagine Hero (sfondo)',
  news_hero_image_url: 'News — URL Immagine Hero (sfondo)',
  recensioni_hero_image_url: 'Recensioni — URL Immagine Hero (sfondo)',
};

const TEXTAREA_FIELDS = ['hero_subtitle', 'about_text', 'chi_siamo_story_p1', 'chi_siamo_story_p2', 'chi_siamo_story_p3', 'chi_siamo_territory_p1', 'chi_siamo_territory_p2', 'chi_siamo_territory_p3', 'timeline_0_desc', 'timeline_1_desc', 'timeline_2_desc', 'timeline_3_desc', 'orari', 'cta_text', 'prodotti_info_p1', 'prodotti_info_p2', 'prodotti_info_p3'];

const IMAGE_FIELDS = ['logo_url', 'hero_image_url', 'about_image_url', 'chi_siamo_hero_image_url', 'chi_siamo_story_image_url', 'chi_siamo_territory_image_url', 'contatti_hero_image_url', 'prodotti_info_img1_url', 'prodotti_info_img2_url', 'prodotti_info_img3_url', 'prodotti_info_img4_url', 'galleria_hero_image_url', 'negozio_hero_image_url', 'news_hero_image_url', 'recensioni_hero_image_url'];
const VIDEO_FIELDS = ['intro_video_url'];

export default function AdminImpostazioni() {
  const [activeTab, setActiveTab] = useState('hero');
  const [form, setForm] = useState({ ...defaultSettings });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setForm({ ...defaultSettings, ...res.data });
      } catch { setForm({ ...defaultSettings }); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleSave = async (sectionId) => {
    const fields = SECTION_FIELDS[sectionId];
    const payload = {};
    fields.forEach(f => { payload[f] = form[f]; });
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings`, payload);
      // Aggiorna il form con i dati confermati dal server
      setForm(prev => ({ ...prev, ...res.data }));
      // Aggiorna il cache del localStorage → le pagine pubbliche non flasheranno più
      writeSettingsCache(res.data);
      toast.success('Impostazioni salvate!');
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const currentFields = SECTION_FIELDS[activeTab];

  return (
    <div className="min-w-0 w-full">
      <div className="mb-4">
        <h2 className="font-heading text-xl font-bold text-gray-900">Impostazioni Sito</h2>
        <p className="font-body text-gray-500 text-xs mt-0.5">Modifica testi, immagini e contenuti</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Tabs — horizontal scroll on mobile, sidebar on desktop */}
        <div className="lg:w-52 flex-shrink-0">
          {/* Mobile: horizontal scrollable pills */}
          <div className="flex lg:hidden gap-1.5 overflow-x-auto pb-1 scrollbar-none mb-2">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-lg font-body text-xs font-medium transition-colors whitespace-nowrap ${activeTab === tab.id ? 'bg-primary text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* Desktop: vertical sidebar */}
          <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full text-left px-4 py-3 font-body text-sm transition-colors border-b border-gray-50 last:border-0 ${activeTab === tab.id ? 'bg-primary text-white font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Fields */}
        <div className="flex-1 min-w-0">
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-gray-900">
                {TABS.find(t => t.id === activeTab)?.label}
              </h3>
              <Button
                onClick={() => handleSave(activeTab)}
                disabled={saving}
                className="bg-primary hover:bg-primary-dark text-white rounded-xl gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Salvataggio...' : 'Salva sezione'}
              </Button>
            </div>

            <div className="space-y-5">
              {currentFields.map(field => (
                <div key={field}>
                  <Label className="font-body text-sm font-medium text-gray-700 mb-1.5 block">
                    {FIELD_LABELS[field] || field}
                  </Label>
                  {TEXTAREA_FIELDS.includes(field) ? (
                    <Textarea
                      value={form[field] || ''}
                      onChange={e => set(field, e.target.value)}
                      rows={3}
                      className="rounded-xl resize-none font-body text-sm"
                    />
                  ) : IMAGE_FIELDS.includes(field) ? (
                    <ImageUploadField
                      value={form[field] || ''}
                      onChange={v => set(field, v)}
                      previewClass="h-28"
                    />
                  ) : VIDEO_FIELDS.includes(field) ? (
                    <>
                      <p className="text-xs text-gray-400 font-body mb-2">
                        Il video viene mostrato a schermo intero la prima volta che il visitatore apre il sito (una volta per sessione). Lascia vuoto per disabilitarlo.
                      </p>
                      <VideoUploadField
                        value={form[field] || ''}
                        onChange={v => set(field, v)}
                      />
                    </>
                  ) : (
                    <Input
                      value={form[field] || ''}
                      onChange={e => set(field, e.target.value)}
                      className="rounded-xl font-body text-sm"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t">
              <Button
                onClick={() => handleSave(activeTab)}
                disabled={saving}
                className="w-full bg-primary hover:bg-primary-dark text-white rounded-xl py-5 gap-2"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                {saving ? 'Salvataggio in corso...' : 'Salva modifiche'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
